// src/features/auth/register/Register.jsx

import { useCallback, useEffect, useState } from 'react';
import { MdEmail, MdLock, MdPerson, MdPhone } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../axios';
import styles from './Register.module.css';

import AgreementField from './components/AgreementField';
import LegalDocumentModal from './components/LegalDocumentModal';
import PasswordField from './components/PasswordField';
import TextField from './components/TextField';

import {
    getInitialFormState,
    mapBackendErrors,
    normalizePhone,
    onlyPhoneChars,
    validateRegisterForm,
} from './register.utils';

const TERMS_ENDPOINT = '/legal-documents/terms-and-conditions';

const PRIVACY_ENDPOINT = '/legal-documents/privacy-policy';

const Register = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState(getInitialFormState());
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [documentsError, setDocumentsError] = useState('');
    const [legalDocuments, setLegalDocuments] = useState({
        terms_and_conditions: null,
        privacy_policy: null,
    });

    const [activeDocumentType, setActiveDocumentType] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);

    const documentsReady = Boolean(legalDocuments?.terms_and_conditions?.id && legalDocuments?.privacy_policy?.id);

    const canAcceptAgreement = form.termsRead && form.privacyRead && documentsReady;

    const clearFieldError = useCallback((fieldName) => {
        setErrors((prev) => ({
            ...prev,
            [fieldName]: '',
            general: '',
        }));
    }, []);

    const loadLegalDocuments = useCallback(async () => {
        try {
            setDocumentsLoading(true);
            setDocumentsError('');

            const [termsResponse, privacyResponse] = await Promise.all([
                axiosInstance.get(TERMS_ENDPOINT, {
                    skipAuthRedirect: true,
                }),

                axiosInstance.get(PRIVACY_ENDPOINT, {
                    skipAuthRedirect: true,
                }),
            ]);

            const termsDocument = termsResponse?.data?.data ?? termsResponse?.data ?? null;

            const privacyDocument = privacyResponse?.data?.data ?? privacyResponse?.data ?? null;

            setLegalDocuments({
                terms_and_conditions: termsDocument,
                privacy_policy: privacyDocument,
            });
        } catch (err) {
            console.error(err);

            setDocumentsError(err?.apiMessage || 'Dokumen legal gagal dimuat.');
        } finally {
            setDocumentsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLegalDocuments();
    }, [loadLegalDocuments]);

    const handleChange = useCallback(
        (event) => {
            const { name, value, type, checked } = event.target;

            setForm((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : name === 'phone' ? onlyPhoneChars(value) : value,
            }));

            clearFieldError(name);
        },
        [clearFieldError]
    );

    const resetForm = useCallback(() => {
        setForm(getInitialFormState());
        setErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
        setActiveDocumentType(null);
        setSelectedDocument(null);
        setIsLegalModalOpen(false);
    }, []);

    const openDocument = useCallback(
        (type) => {
            const document = legalDocuments?.[type];

            if (!document?.content) {
                setErrors((prev) => ({
                    ...prev,
                    general: 'Dokumen belum tersedia.',
                }));

                return;
            }

            setSelectedDocument(document);
            setActiveDocumentType(type);
            setIsLegalModalOpen(true);
        },
        [legalDocuments]
    );

    const closeDocument = useCallback(() => {
        setIsLegalModalOpen(false);
        setSelectedDocument(null);
        setActiveDocumentType(null);
    }, []);

    const confirmReadDocument = useCallback(() => {
        if (!selectedDocument) return;

        setForm((prev) => ({
            ...prev,
            termsRead: selectedDocument.type === 'terms_and_conditions' ? true : prev.termsRead,
            privacyRead: selectedDocument.type === 'privacy_policy' ? true : prev.privacyRead,
        }));

        setErrors((prev) => ({
            ...prev,
            termsRead: '',
            privacyRead: '',
            general: '',
        }));

        closeDocument();
    }, [closeDocument, selectedDocument]);

    const handleSubmit = useCallback(
        async (event) => {
            event.preventDefault();

            const validationErrors = validateRegisterForm(form, documentsReady);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            setLoading(true);
            setErrors({});

            try {
                const payload = {
                    name: form.name.trim(),
                    email: form.email.trim() || null,
                    phone_number: normalizePhone(form.phone),
                    password: form.password,
                    password_confirmation: form.confirmPassword,
                    legal_documents: {
                        terms_and_conditions: legalDocuments.terms_and_conditions?.id || null,
                        privacy_policy: legalDocuments.privacy_policy?.id || null,
                    },
                };

                const response = await axiosInstance.post('/auth/register', payload);

                if (response?.data?.success) {
                    const phone = payload.phone_number;
                    const name = payload.name;

                    sessionStorage.setItem('mp_verify_phone', phone);
                    sessionStorage.setItem('mp_verify_name', name);

                    resetForm();

                    navigate('/verify-otp', {
                        state: {
                            phone,
                            name,
                            context: 'register',
                        },
                    });
                    return;
                }

                setErrors({
                    general: 'Pendaftaran gagal. Silakan coba lagi.',
                });
            } catch (err) {
                const status = err?.response?.status;
                const responseData = err?.response?.data;

                if (status === 422) {
                    setErrors(mapBackendErrors(responseData));
                } else {
                    setErrors({
                        general: responseData?.message || err?.apiMessage || 'Pendaftaran gagal. Silakan coba lagi.',
                    });
                }
            } finally {
                setLoading(false);
            }
        },
        [documentsReady, form, legalDocuments, navigate, resetForm]
    );

    return (
        <>
            <div className={styles.page}>
                <section className={styles.formSide}>
                    <div className={`${styles.formCard} ${styles.registerFormCard}`}>
                        <div className={styles.logoWrap}>
                            <img src="/logo-dark.png" alt="Logo" className={styles.logo} />
                        </div>

                        <div className={styles.formHeader}>
                            <h2 className={styles.formTitle}>Buat Akun Baru</h2>

                            <p className={styles.formSub}>
                                Sudah punya akun?{' '}
                                <Link to="/login" className={styles.link}>
                                    Masuk
                                </Link>
                            </p>
                        </div>

                        {documentsError && <div className={styles.warningBox}>{documentsError}</div>}

                        {errors.general && <div className={styles.errorBox}>{errors.general}</div>}

                        <form onSubmit={handleSubmit} noValidate className={styles.form}>
                            <div className={styles.registerGrid}>
                                <TextField
                                    label="Nama Lengkap"
                                    icon={<MdPerson />}
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Nama lengkap"
                                    error={errors.name}
                                    autoComplete="name"
                                />

                                <TextField
                                    label="Email"
                                    icon={<MdEmail />}
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="Email (opsional)"
                                    error={errors.email}
                                    autoComplete="email"
                                />

                                <TextField
                                    label="Nomor WhatsApp"
                                    icon={<MdPhone />}
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="08xx-xxxx-xxxx"
                                    error={errors.phone}
                                    autoComplete="tel"
                                    inputMode="tel"
                                />

                                <PasswordField
                                    label="Kata Sandi"
                                    icon={<MdLock />}
                                    name="password"
                                    value={form.password}
                                    onChange={handleChange}
                                    show={showPassword}
                                    setShow={setShowPassword}
                                    error={errors.password}
                                    autoComplete="new-password"
                                />

                                <PasswordField
                                    label="Konfirmasi Kata Sandi"
                                    icon={<MdLock />}
                                    name="confirmPassword"
                                    value={form.confirmPassword}
                                    onChange={handleChange}
                                    show={showConfirmPassword}
                                    setShow={setShowConfirmPassword}
                                    error={errors.confirmPassword}
                                    autoComplete="new-password"
                                />

                                <AgreementField
                                    checked={form.agreeTerms}
                                    onChange={handleChange}
                                    error={errors.agreeTerms || errors.termsRead || errors.privacyRead}
                                    loading={loading || documentsLoading}
                                    canAcceptAgreement={canAcceptAgreement}
                                    termsRead={form.termsRead}
                                    privacyRead={form.privacyRead}
                                    onOpenTerms={() => openDocument('terms_and_conditions')}
                                    onOpenPrivacy={() => openDocument('privacy_policy')}
                                    documentsLoading={documentsLoading}
                                />
                            </div>

                            <button type="submit" className={styles.submitBtn} disabled={loading || !form.agreeTerms}>
                                {loading ? <span className={styles.spinner} aria-hidden="true" /> : 'Daftar Sekarang'}
                            </button>

                            <p className={styles.submitNote}>
                                Dengan melanjutkan, Anda menyatakan telah membaca dan menyetujui dokumen legal platform.
                            </p>
                        </form>
                    </div>
                </section>
            </div>

            <LegalDocumentModal
                open={isLegalModalOpen}
                document={selectedDocument}
                onClose={closeDocument}
                onConfirmRead={confirmReadDocument}
            />
        </>
    );
};

export default Register;
